import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Textarea } from './ui/textarea';
import { Progress } from './ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Heart,
  Apple,
  Moon,
  Activity,
  Plus,
  Calendar,
  TrendingUp,
  CheckCircle2,
  XCircle,
  Target,
  Flame,
  Droplets,
  Coffee,
} from 'lucide-react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart,
  Bar,
} from 'recharts';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

// Datos de dieta
const dietaData = [
  { fecha: '2026-01-22', desayuno: true, comida: true, cena: true, snacks: 1, agua: 2.5, calidad: 'buena' },
  { fecha: '2026-01-21', desayuno: true, comida: true, cena: true, snacks: 2, agua: 2.0, calidad: 'regular' },
  { fecha: '2026-01-20', desayuno: false, comida: true, cena: true, snacks: 3, agua: 1.5, calidad: 'mala' },
  { fecha: '2026-01-19', desayuno: true, comida: true, cena: true, snacks: 1, agua: 3.0, calidad: 'buena' },
  { fecha: '2026-01-18', desayuno: true, comida: true, cena: false, snacks: 2, agua: 2.0, calidad: 'regular' },
];

// Datos de sueño
const suenoData = [
  { fecha: '2026-01-22', horas: 7.5, calidad: 'buena', horaDormir: '23:30', horaDespertar: '07:00' },
  { fecha: '2026-01-21', horas: 6.0, calidad: 'regular', horaDormir: '00:30', horaDespertar: '06:30' },
  { fecha: '2026-01-20', horas: 8.0, calidad: 'excelente', horaDormir: '23:00', horaDespertar: '07:00' },
  { fecha: '2026-01-19', horas: 5.5, calidad: 'mala', horaDormir: '01:00', horaDespertar: '06:30' },
  { fecha: '2026-01-18', horas: 7.0, calidad: 'buena', horaDormir: '23:45', horaDespertar: '06:45' },
];

// Datos de ejercicio
const ejercicioData = [
  { fecha: '2026-01-22', tipo: 'Gimnasio', duracion: 60, intensidad: 'alta', calorias: 450 },
  { fecha: '2026-01-21', tipo: 'Descanso', duracion: 0, intensidad: '-', calorias: 0 },
  { fecha: '2026-01-20', tipo: 'Running', duracion: 45, intensidad: 'media', calorias: 380 },
  { fecha: '2026-01-19', tipo: 'Gimnasio', duracion: 75, intensidad: 'alta', calorias: 520 },
  { fecha: '2026-01-18', tipo: 'Yoga', duracion: 30, intensidad: 'baja', calorias: 120 },
];

// Datos para gráficos
const suenoSemanal = [
  { dia: 'Lun', horas: 7.5 },
  { dia: 'Mar', horas: 6.0 },
  { dia: 'Mié', horas: 8.0 },
  { dia: 'Jue', horas: 5.5 },
  { dia: 'Vie', horas: 7.0 },
  { dia: 'Sáb', horas: 8.5 },
  { dia: 'Dom', horas: 7.0 },
];

const ejercicioSemanal = [
  { dia: 'Lun', minutos: 60 },
  { dia: 'Mar', minutos: 0 },
  { dia: 'Mié', minutos: 45 },
  { dia: 'Jue', minutos: 75 },
  { dia: 'Vie', minutos: 30 },
  { dia: 'Sáb', minutos: 90 },
  { dia: 'Dom', minutos: 0 },
];

const calidadConfig = {
  excelente: { label: 'Excelente', color: 'bg-emerald-100 text-emerald-700' },
  buena: { label: 'Buena', color: 'bg-blue-100 text-blue-700' },
  regular: { label: 'Regular', color: 'bg-amber-100 text-amber-700' },
  mala: { label: 'Mala', color: 'bg-red-100 text-red-700' },
};

export function MindsetPage() {
  const [activeTab, setActiveTab] = useState('resumen');

  // Cálculos de estadísticas
  const promedioSueno = (suenoData.reduce((sum, d) => sum + d.horas, 0) / suenoData.length).toFixed(1);
  const diasEjercicio = ejercicioData.filter(d => d.duracion > 0).length;
  const promedioAgua = (dietaData.reduce((sum, d) => sum + d.agua, 0) / dietaData.length).toFixed(1);
  const diasDietaBuena = dietaData.filter(d => d.calidad === 'buena').length;

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
      data-testid="mindset-page"
    >
      {/* Header */}
      <motion.div variants={itemVariants} className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Mindset</h1>
          <p className="text-muted-foreground mt-1">Valores y registro de bienestar</p>
        </div>
        <Badge variant="outline" className="text-primary border-primary">
          <Calendar className="w-3 h-3 mr-1" />
          Esta Semana
        </Badge>
      </motion.div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid grid-cols-4 w-full max-w-lg">
          <TabsTrigger value="resumen">
            <Heart className="w-4 h-4 mr-2" />
            Resumen
          </TabsTrigger>
          <TabsTrigger value="dieta">
            <Apple className="w-4 h-4 mr-2" />
            Dieta
          </TabsTrigger>
          <TabsTrigger value="sueno">
            <Moon className="w-4 h-4 mr-2" />
            Sueño
          </TabsTrigger>
          <TabsTrigger value="ejercicio">
            <Activity className="w-4 h-4 mr-2" />
            Ejercicio
          </TabsTrigger>
        </TabsList>

        {/* Resumen Tab */}
        <TabsContent value="resumen" className="space-y-6">
          {/* KPIs */}
          <motion.div variants={itemVariants} className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="card-hover bg-gradient-to-br from-blue-50 to-blue-100">
              <CardContent className="p-4 text-center">
                <Moon className="w-8 h-8 mx-auto text-blue-600 mb-2" />
                <p className="text-xs text-muted-foreground">Promedio Sueño</p>
                <p className="text-2xl font-bold text-blue-700">{promedioSueno}h</p>
              </CardContent>
            </Card>
            <Card className="card-hover bg-gradient-to-br from-emerald-50 to-emerald-100">
              <CardContent className="p-4 text-center">
                <Activity className="w-8 h-8 mx-auto text-emerald-600 mb-2" />
                <p className="text-xs text-muted-foreground">Días Ejercicio</p>
                <p className="text-2xl font-bold text-emerald-700">{diasEjercicio}/7</p>
              </CardContent>
            </Card>
            <Card className="card-hover bg-gradient-to-br from-cyan-50 to-cyan-100">
              <CardContent className="p-4 text-center">
                <Droplets className="w-8 h-8 mx-auto text-cyan-600 mb-2" />
                <p className="text-xs text-muted-foreground">Promedio Agua</p>
                <p className="text-2xl font-bold text-cyan-700">{promedioAgua}L</p>
              </CardContent>
            </Card>
            <Card className="card-hover bg-gradient-to-br from-amber-50 to-amber-100">
              <CardContent className="p-4 text-center">
                <Apple className="w-8 h-8 mx-auto text-amber-600 mb-2" />
                <p className="text-xs text-muted-foreground">Dieta Buena</p>
                <p className="text-2xl font-bold text-amber-700">{diasDietaBuena}/5</p>
              </CardContent>
            </Card>
          </motion.div>

          {/* Gráficos */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <motion.div variants={itemVariants}>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Moon className="w-5 h-5 text-blue-600" />
                    Horas de Sueño (Semana)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[200px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={suenoSemanal}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="dia" fontSize={12} />
                        <YAxis fontSize={12} domain={[0, 10]} />
                        <Tooltip />
                        <Bar dataKey="horas" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div variants={itemVariants}>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="w-5 h-5 text-emerald-600" />
                    Minutos de Ejercicio (Semana)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[200px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={ejercicioSemanal}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="dia" fontSize={12} />
                        <YAxis fontSize={12} />
                        <Tooltip />
                        <Bar dataKey="minutos" fill="#10b981" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Registro rápido */}
          <motion.div variants={itemVariants}>
            <Card className="bg-gradient-to-r from-primary/5 to-primary/10">
              <CardHeader>
                <CardTitle>Registro de Hoy</CardTitle>
                <CardDescription>Actualiza tus métricas diarias</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 bg-white rounded-lg text-center">
                    <Moon className="w-6 h-6 mx-auto text-blue-600 mb-2" />
                    <p className="text-sm font-medium">Sueño</p>
                    <Input type="number" placeholder="7.5" className="mt-2 text-center" />
                    <p className="text-xs text-muted-foreground mt-1">horas</p>
                  </div>
                  <div className="p-4 bg-white rounded-lg text-center">
                    <Activity className="w-6 h-6 mx-auto text-emerald-600 mb-2" />
                    <p className="text-sm font-medium">Ejercicio</p>
                    <Input type="number" placeholder="60" className="mt-2 text-center" />
                    <p className="text-xs text-muted-foreground mt-1">minutos</p>
                  </div>
                  <div className="p-4 bg-white rounded-lg text-center">
                    <Droplets className="w-6 h-6 mx-auto text-cyan-600 mb-2" />
                    <p className="text-sm font-medium">Agua</p>
                    <Input type="number" placeholder="2.5" className="mt-2 text-center" step="0.5" />
                    <p className="text-xs text-muted-foreground mt-1">litros</p>
                  </div>
                </div>
                <Button className="w-full mt-4 gradient-primary text-white">
                  <Plus className="w-4 h-4 mr-2" />
                  Guardar Registro
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        {/* Dieta Tab */}
        <TabsContent value="dieta" className="space-y-6">
          <motion.div variants={itemVariants}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Registro de Dieta</h2>
              <Button className="gradient-primary text-white">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Registro
              </Button>
            </div>
            <Card>
              <CardContent className="p-0">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="text-left py-3 px-4 text-sm font-medium">Fecha</th>
                      <th className="text-center py-3 px-4 text-sm font-medium">Desayuno</th>
                      <th className="text-center py-3 px-4 text-sm font-medium">Comida</th>
                      <th className="text-center py-3 px-4 text-sm font-medium">Cena</th>
                      <th className="text-center py-3 px-4 text-sm font-medium">Snacks</th>
                      <th className="text-center py-3 px-4 text-sm font-medium">Agua</th>
                      <th className="text-left py-3 px-4 text-sm font-medium">Calidad</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dietaData.map((dia, i) => (
                      <tr key={i} className="border-b hover:bg-muted/20">
                        <td className="py-3 px-4 font-medium">
                          {new Date(dia.fecha).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric' })}
                        </td>
                        <td className="py-3 px-4 text-center">
                          {dia.desayuno ? <CheckCircle2 className="w-5 h-5 text-emerald-500 mx-auto" /> : <XCircle className="w-5 h-5 text-red-400 mx-auto" />}
                        </td>
                        <td className="py-3 px-4 text-center">
                          {dia.comida ? <CheckCircle2 className="w-5 h-5 text-emerald-500 mx-auto" /> : <XCircle className="w-5 h-5 text-red-400 mx-auto" />}
                        </td>
                        <td className="py-3 px-4 text-center">
                          {dia.cena ? <CheckCircle2 className="w-5 h-5 text-emerald-500 mx-auto" /> : <XCircle className="w-5 h-5 text-red-400 mx-auto" />}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Badge variant="outline">{dia.snacks}</Badge>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className="flex items-center justify-center gap-1">
                            <Droplets className="w-4 h-4 text-cyan-500" />
                            {dia.agua}L
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={calidadConfig[dia.calidad]?.color}>
                            {calidadConfig[dia.calidad]?.label}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        {/* Sueño Tab */}
        <TabsContent value="sueno" className="space-y-6">
          <motion.div variants={itemVariants}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Registro de Sueño</h2>
              <Button className="gradient-primary text-white">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Registro
              </Button>
            </div>
            <Card>
              <CardContent className="p-0">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="text-left py-3 px-4 text-sm font-medium">Fecha</th>
                      <th className="text-center py-3 px-4 text-sm font-medium">Horas</th>
                      <th className="text-center py-3 px-4 text-sm font-medium">Hora Dormir</th>
                      <th className="text-center py-3 px-4 text-sm font-medium">Hora Despertar</th>
                      <th className="text-left py-3 px-4 text-sm font-medium">Calidad</th>
                    </tr>
                  </thead>
                  <tbody>
                    {suenoData.map((dia, i) => (
                      <tr key={i} className="border-b hover:bg-muted/20">
                        <td className="py-3 px-4 font-medium">
                          {new Date(dia.fecha).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric' })}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className="font-semibold">{dia.horas}h</span>
                        </td>
                        <td className="py-3 px-4 text-center text-muted-foreground">{dia.horaDormir}</td>
                        <td className="py-3 px-4 text-center text-muted-foreground">{dia.horaDespertar}</td>
                        <td className="py-3 px-4">
                          <Badge className={calidadConfig[dia.calidad]?.color}>
                            {calidadConfig[dia.calidad]?.label}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        {/* Ejercicio Tab */}
        <TabsContent value="ejercicio" className="space-y-6">
          <motion.div variants={itemVariants}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Registro de Ejercicio</h2>
              <Button className="gradient-primary text-white">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Registro
              </Button>
            </div>
            <Card>
              <CardContent className="p-0">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="text-left py-3 px-4 text-sm font-medium">Fecha</th>
                      <th className="text-left py-3 px-4 text-sm font-medium">Tipo</th>
                      <th className="text-center py-3 px-4 text-sm font-medium">Duración</th>
                      <th className="text-center py-3 px-4 text-sm font-medium">Intensidad</th>
                      <th className="text-center py-3 px-4 text-sm font-medium">Calorías</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ejercicioData.map((dia, i) => (
                      <tr key={i} className="border-b hover:bg-muted/20">
                        <td className="py-3 px-4 font-medium">
                          {new Date(dia.fecha).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric' })}
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant={dia.tipo === 'Descanso' ? 'secondary' : 'outline'}>{dia.tipo}</Badge>
                        </td>
                        <td className="py-3 px-4 text-center">
                          {dia.duracion > 0 ? `${dia.duracion} min` : '-'}
                        </td>
                        <td className="py-3 px-4 text-center">
                          {dia.intensidad !== '-' && (
                            <Badge className={
                              dia.intensidad === 'alta' ? 'bg-red-100 text-red-700' :
                              dia.intensidad === 'media' ? 'bg-amber-100 text-amber-700' :
                              'bg-emerald-100 text-emerald-700'
                            }>
                              {dia.intensidad}
                            </Badge>
                          )}
                        </td>
                        <td className="py-3 px-4 text-center">
                          {dia.calorias > 0 && (
                            <span className="flex items-center justify-center gap-1">
                              <Flame className="w-4 h-4 text-orange-500" />
                              {dia.calorias}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>
      </Tabs>
    </motion.div>
  );
}
